import Settings from '../domain/Settings';
import {getBreakpoints} from './breakpoints';

let sizeMappings: SizeMappings = {};

export type SizeMappings = {
	[type: string]: SizeMapping
}

export type SizeMapping = {
	[breakpoint: string]: Sizes
		// The others here are because this is requird for index types in TS…
		| CustomBreakpointMapping | SizeRemapping
	customBreakpointMapping?: CustomBreakpointMapping
	sizeRemapping?: SizeRemapping
};

export type Sizes = Array<[number, number]>;
export type CustomBreakpointMapping = Array<{ minWidth: number, sizes: Sizes }>;
export type SizeRemapping = {
	[mediaType: string]: Array<[[number | '*', number | '*'], [number, number]]>
};

function init() {
	const settings = Settings.getInstance();

	_validateSizeMappings(settings.sizeMapping);

	sizeMappings = JSON.parse(JSON.stringify(settings.sizeMapping));
}

function _isTwoValidDimensions(size: unknown): size is [number, number] {
	return Array.isArray(size) && size.length === 2
		&& typeof size[0] === 'number' && typeof size[1] === 'number'
		&& size[0] > 0 && size[1] > 0;
}

function _validateSizeMappings(sms: SizeMappings) {
	const breakpointNames = Object.keys(getBreakpoints());

	Object.keys(sms).forEach((type) => {
		Object.keys(sms[type]).forEach((breakpoint) => {
			const sizeMapping = sms[type][breakpoint];

			if (breakpointNames.indexOf(breakpoint) === -1) {
				return;
			}

			if (!Array.isArray(sizeMapping)) {
				throw new Error(`Breakpoint '${breakpoint}' of size mapping '${type}' isn't an array.`);
			}

			sizeMapping.forEach((size) => {
				if (_isTwoValidDimensions(size)) {
					return;
				}

				throw new Error(`Invalid size in breakpoint '${breakpoint}' of size mapping '${type}'.`);
			});
		});
	});
}

function getSizeMapping(type: string): SizeMapping {
	return typeof sizeMappings[type] === 'undefined' ? {} : sizeMappings[type];
}

function getBreakpointSizeMapping(type: string): Record<string, Sizes> {
	const sizeMapping = getSizeMapping(type);

	return Object.fromEntries(Object.keys(getBreakpoints()).map((bp) => [bp, sizeMapping?.[bp] as Sizes]));
}

function getCustomBreakpointMapping(type: string): CustomBreakpointMapping | undefined {
	const sizeMapping = getSizeMapping(type);

	return sizeMapping.customBreakpointMapping as CustomBreakpointMapping;
}

function getSizesFromSizeMapForBreakpoint(type: string, currentBreakpoint: string): Sizes | undefined {
	const sizeMapping = getSizeMapping(type),
		sortedBreakpoints = Object.entries(getBreakpoints()).sort(([, b1], [, b2]) => b1 === b2 ? 0 : b1 < b2 ? -1 : 1);
	let sizes = sizeMapping[currentBreakpoint];
	const customMappings = sizeMapping.customBreakpointMapping as CustomBreakpointMapping;

	if (customMappings?.length) {
		customMappings.sort((a, b) => a.minWidth < b.minWidth ? -1 : 1)
			.forEach((sizeMap) => {
				if (window.innerWidth >= sizeMap.minWidth) {
					for (let i = sortedBreakpoints.length - 1; i >= 0; i--) {
						if (sortedBreakpoints[i][1] <= sizeMap.minWidth && sortedBreakpoints[i][0] === currentBreakpoint) {
							sizes = sizeMap.sizes;
							break;
						}
					}
				}
			});
	}

	return sizes as Sizes | undefined;
}

export default {
	init,
	getSizeMapping,
	getSizesFromSizeMapForBreakpoint,
	getBreakpointSizeMapping,
	getCustomBreakpointMapping
};
